import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as splitTest from "splitTest";
import XRegExp from "xregexp";
import * as browserTTSVoices from "./browserTTSVoices";

type JQueryElement = any;

/**
 * This contains alternate language codes that are used by the web speech API voices instead
 * of the language code we normally use.
 */
const alternativeLanguageCodes = {
  no: "nb",
};

interface TTSVoice {
  voiceURI: string;
  lang: string;
  name: string;
  localService: boolean;
  default: boolean;
}

/**
 * You need to call this before using TTS for a given language.
 * Returns list of voices for the given language.
 */
export const initialize = async (
  language: string
): Promise<{
  ttsVoices: { voiceURI: string; lang: string; displayName: string }[];
  voiceSelected: boolean;
}> => {
  if (!isTTSSupported(language)) {
    return {
      ttsVoices: [],
      voiceSelected: false,
    };
  }

  const voices = await getVoicesForLanguage(language);

  const ttsVoices = voices.map((voice) => {
    const languageCode = voice.lang.substring(0, 2);
    const languageInfo = languageMap.map[languageCode];

    /** @type {string} */
    let displayName;

    if (languageInfo) {
      const nameLower = voice.name.toLowerCase();
      if (
        nameLower.includes(languageInfo.name.toLowerCase()) ||
        nameLower.includes(languageInfo.english.toLowerCase())
      ) {
        // Reformat the macOS Chrome voice names, which are super ugly with nested parentheses
        const chromeVoiceNameRegExp = new XRegExp(
          "(\\p{L}+) \\((\\p{L}+) \\((\\p{L}+)\\)\\)"
        );
        const match = chromeVoiceNameRegExp.exec(voice.name);

        if (match) {
          displayName = `${match[1]} (${match[2]}, ${match[3]})`;
        } else {
          // The voice name already includes the language code, so we don't need to add it.
          displayName = voice.name;
        }
      }
    }

    if (!displayName) {
      displayName = `${voice.name} (${voice.lang})`;
    }

    return {
      voiceURI: voice.voiceURI,
      lang: voice.lang,
      displayName,
    };
  });

  const voiceSelected = (await getVoice(language, false)) ? true : false;

  return {
    ttsVoices,
    voiceSelected,
  };
};

export const getSupportedLanguages = async () => {
  const voices = await getVoices();
  return new Set(voices.map((voice) => voice.lang.split("-")[0]));
};

/** This assumes that the given @param voices are all of the correct language */
export const pickDefaultVoice = (
  language: string,
  voices: { voiceURI: string; lang: string }[]
) => {
  switch (language) {
    case "es": {
      // Prefer European Spanish voice based on user's timezone
      const preferEuropean = dateUtils.getTimezoneOffset() < 180;
      let preferredVoice;

      if (preferEuropean) {
        preferredVoice =
          voices.find(
            (voice) =>
              voice.voiceURI === "Monica" || voice.voiceURI.startsWith("Mónica")
          ) ?? voices.find((voice) => voice.lang === "es-ES");
      } else {
        preferredVoice =
          voices.find((voice) => voice.voiceURI.startsWith("Paulina")) ??
          voices.find((voice) => voice.lang === "es-MX");
      }

      return preferredVoice ?? voices[0];
    }
    default:
      return voices[0];
  }
};

const getVoices = (): Promise<TTSVoice[]> => {
  return new Promise((resolve) => {
    if (window.speechSynthesis === undefined) {
      resolve([]);
      return;
    }

    const voices = window.speechSynthesis.getVoices() ?? [];

    if (voices.length > 0) {
      resolve(voices);
      return;
    }

    const onVoicesChanged = () => {
      clearTimeout(timeoutId);
      resolve(window.speechSynthesis.getVoices());
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        onVoicesChanged
      );
    };

    // Wait for voices to be loaded
    window.speechSynthesis.addEventListener("voiceschanged", onVoicesChanged);

    // If voiceschanged has not triggered within 30 seconds, resolve
    const timeoutId = setTimeout(() => {
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        onVoicesChanged
      );
      resolve(window.speechSynthesis.getVoices());
    }, 30 * 1000);
  });
};

// Doing this because remote voices are not generated the onboundary event
const useLocalVoicesOnly = false;

/** Filters out bad voices on iOS */
const filteredVoices = (voices: any[]) => {
  // Use map to avoid duplicates
  const uniqueVoices = new Map<string, any>();

  for (const voice of voices) {
    // com.apple.eloquence voices all sound bad
    if (voice.voiceURI.startsWith("com.apple.eloquence")) {
      continue;
    }

    // If voice URI has already been added, skip
    if (uniqueVoices.has(voice.voiceURI)) {
      continue;
    }

    // com.apple.voice.super-compact voices can be OK, but if an equivalent compact voice exists,
    // use that instead
    if (voice.voiceURI.startsWith("com.apple.voice.super-compact")) {
      // If the compact voice exists, don't add this super-compact voice
      const equivalentCompactURI = voice.voiceURI.replace(
        "super-compact",
        "compact"
      );
      if (
        voices.some(
          (otherVoice) => otherVoice.voiceURI === equivalentCompactURI
        )
      ) {
        continue;
      }
    }

    uniqueVoices.set(voice.voiceURI, voice);
  }

  return Array.from(uniqueVoices.values()).sort((a, b) =>
    a.voiceURI < b.voiceURI ? -1 : 1
  );
};

const cachedVoicesForLanguage = new Map<string, any[]>();

const getVoicesForLanguage = async (language: string): Promise<TTSVoice[]> => {
  const cachedResult = cachedVoicesForLanguage.get(language);
  if (cachedResult) {
    return cachedResult;
  }

  const alternativeLanguageCode = alternativeLanguageCodes[language];
  let voices = await getVoices();
  voices = voices.filter(
    (voice) =>
      (!useLocalVoicesOnly || voice.localService) &&
      (voice.lang.startsWith(language) ||
        (alternativeLanguageCode &&
          voice.lang.startsWith(alternativeLanguageCode)))
  );
  const result = filteredVoices(voices);
  cachedVoicesForLanguage[language] = result;

  return result;
};

const normalize = (text: string) => {
  return text.replace(/’/g, "'").replace(/\n+/g, "\n").replace(/["""]/g, "");
};

interface TextPart {
  text: string;
  /** Starting character index of `text` within the larger sentence */
  charIndex: number;
  /** Position within the book (or undefined if not a translatable word) */
  position: number | undefined;
}

/**
 * Extracts the text from the JQuery nodes, with a mapping of parts of the text to the position
 * within the entire book/text.
 */
const extractText = (
  nodes: {
    node: JQueryElement;
    text: string;
    range: { from: number; to: number };
  }[]
): { text: string; parts: TextPart[] } => {
  const parts: TextPart[] = [];
  let text = "";
  let currentCharIndex = 0;

  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];

    let normalizedText = node.text ? normalize(node.text) : "";

    if (!node.text) {
      console.log("empty node: ", node);
    }

    if (index === nodes.length - 1) {
      normalizedText = cleanTextForTTS(normalizedText);
    }

    text += normalizedText;

    parts.push({
      text: normalizedText,
      charIndex: currentCharIndex,
      position: node.range?.to ?? undefined,
    });
    currentCharIndex += normalizedText.length;
  }

  return { text, parts };
};

const cleanTextForTTS = (text: string) => {
  // Replace additional punctuation at the end of the text:
  return text.replace(/[.!?][ .!?].$/, "");
};

let onEndHandler: () => void;

export const playTTSSimple = async (languageCode: string, text: string) => {
  cancelTTS();
  const voice = await getVoice(languageCode);

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = languageCode;
  utterance.rate = getRate(languageCode);
  utterance.voice = voice;
  window.speechSynthesis.speak(utterance);
};

const getVoice = async (
  languageCode: string,
  fallback: boolean = true
): Promise<SpeechSynthesisVoice> => {
  const voiceURI = browserTTSVoices.getVoice(languageCode);
  const alternativeLanguageCode = alternativeLanguageCodes[languageCode];

  if (voiceURI) {
    const voice = (await getVoices()).filter(
      (candidateVoice) =>
        (candidateVoice.lang.startsWith(languageCode) ||
          (alternativeLanguageCode &&
            candidateVoice.lang.startsWith(alternativeLanguageCode))) &&
        candidateVoice.voiceURI === voiceURI
    )[0];

    if (voice) {
      return voice;
    }
  }

  if (!fallback) {
    return undefined;
  }

  // No voice was found, just pick the first one that matches the language
  return pickDefaultVoice(
    languageCode,
    await getVoicesForLanguage(languageCode)
  );
};

const getRate = (languageCode: string) => {
  const readAloudRate = parseFloat(browserTTSVoices.getRate(languageCode));

  /** This is multiplied to the presented rate since they tend to be too fast. */
  const extraFactor = 0.85;

  return (isNaN(readAloudRate) ? 0.8 : readAloudRate) * extraFactor;
};

export const playTTS = async (
  languageCode: string,
  // voiceURI: string,
  nodes: {
    node: JQueryElement[];
    text: string;
    range: { from: number; to: number };
  }[],
  // eslint-disable-next-line no-unused-vars
  onBoundary?: (position: number) => void,
  // eslint-disable-next-line no-unused-vars
  onEnd?: (nextPosition: number) => void,
  onError?: () => void
) => {
  const { text, parts } = extractText(nodes);

  // Check if speech synthesis is supported
  if (!("speechSynthesis" in window)) {
    console.error("Your browser does not support speech synthesis.");
    return;
  }

  const utterance = new SpeechSynthesisUtterance(text);

  // Set the language for the utterance
  utterance.lang = languageCode;

  utterance.voice = await getVoice(languageCode);
  utterance.rate = getRate(languageCode);

  // Reverse array
  parts.reverse();

  utterance.onboundary = (event) => {
    const charIndex = event.charIndex;

    const part = parts.find(
      (candidatePart) =>
        candidatePart.charIndex <= charIndex &&
        candidatePart.position !== undefined
    );

    if (part) {
      onBoundary(part.position);
    }
  };

  onEndHandler = () => {
    onEnd?.(
      parts.find((part) => part.position !== undefined)?.position + 1 ??
        undefined
    );
  };
  utterance.addEventListener("end", onEndHandler);
  utterance.addEventListener("error", () => {
    onError?.();
  });

  // Trying this in case it fixes issue on iOS Safari: https://stackoverflow.com/a/35935851
  window.READLANG = window.READLANG ?? {};
  window.READLANG.utterance = utterance;

  // Use the Web Speech API to play the utterance
  window.speechSynthesis.speak(utterance);

  splitTest.trackEventWithProperties("readAloud", {
    voice: utterance.voice.voiceURI,
  });
};

export const cancelTTS = () => {
  if ("speechSynthesis" in window) {
    // Remove onend handler to prevent next sentence from being read upon cancel
    window.READLANG?.utterance?.removeEventListener("end", onEndHandler);
    window.speechSynthesis.cancel();
  } else {
    console.error("Your browser does not support speech synthesis.");
  }
};

/**
 * Checks if TTS is supported on this browser. Doesn't check that a suitable voice is actually
 * available
 */
const isTTSSupported = async (language: string) => {
  if (!("speechSynthesis" in window)) {
    return false;
  }

  if (languageMap.map[language].noWordBoundaries) {
    return false;
  }

  // Regex to match the Firefox browser version number in the useragent string (if it exists)
  const firefoxVersionMatch = navigator.userAgent.match(/Firefox\/(\d+)/);

  // Check if Firefox version is at least 117 (when I can verify that speech synthesis works, in an
  // earlier version setting specific voices didn't work)
  if (firefoxVersionMatch && parseInt(firefoxVersionMatch[1], 10) < 117) {
    return false;
  }

  return true;
};

export const playVoiceDemo = async (language) => {
  const currentVoiceURI = browserTTSVoices.getVoice(language);

  const currentVoice = (await getVoicesForLanguage(language)).find(
    (voice) => voice.voiceURI === currentVoiceURI
  );

  playTTSSimple(
    language,
    languageMap.map[language].ttsVoiceSelectedText.replace(
      "{{voiceName}}",
      currentVoice.name
    )
  );
};
